import { useMemo, useState } from 'react';
import { submitServiceSpecContribution } from '../../parts/api';

const lines = (value) => (Array.isArray(value) ? value.join('\n') : '');
const fromLines = (value) => String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);
const blankStep = () => ({ kind: 'torque', value: '', unit: 'Nm', label: '', originalText: '' });

const ServiceSpecContributionModal = ({ modelSlug, sectionCode, part, mapping = null, onClose, onSubmitted }) => {
  const existing = mapping?.specification;
  const submissionType = existing ? 'correction' : 'missing_spec';
  const initial = useMemo(() => ({
    specType: existing?.specType || 'tightening_torque',
    pilotArea: existing?.pilotArea || 'community',
    component: existing?.component || part.description || '',
    connection: existing?.connection || '',
    fastener: { type: existing?.fastener?.type || '', size: existing?.fastener?.size || '' },
    steps: (existing?.steps || [blankStep()]).map((step) => ({ ...step })),
    conditions: lines(existing?.conditions),
    cautions: lines(existing?.cautions),
    applicability: {
      yearStart: existing?.applicability?.yearStart || '',
      yearEnd: existing?.applicability?.yearEnd || '',
      qualifiers: lines(existing?.applicability?.qualifiers),
    },
    source: {
      document: existing?.source?.document || '',
      pdfPage: existing?.source?.pdfPage || '',
      manualSection: existing?.source?.manualSection || '',
      quote: existing?.source?.quote || '',
    },
  }), [existing, part.description]);
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const changeNested = (group, field, value) => setForm((current) => ({ ...current, [group]: { ...current[group], [field]: value } }));
  const changeStep = (index, field, value) => setForm((current) => ({
    ...current,
    steps: current.steps.map((step, stepIndex) => stepIndex === index ? { ...step, [field]: value } : step),
  }));

  const submit = async (event) => {
    event.preventDefault();
    try {
      setBusy(true);
      setStatus('');
      const proposedSpecification = {
        ...form,
        steps: form.steps.map((step) => ({ ...step, value: Number(step.value) })),
        conditions: fromLines(form.conditions),
        cautions: fromLines(form.cautions),
        applicability: {
          yearStart: form.applicability.yearStart ? Number(form.applicability.yearStart) : undefined,
          yearEnd: form.applicability.yearEnd ? Number(form.applicability.yearEnd) : undefined,
          qualifiers: fromLines(form.applicability.qualifiers),
        },
        source: { ...form.source, pdfPage: Number(form.source.pdfPage) },
      };
      await submitServiceSpecContribution({
        submissionType,
        existingSpecKey: mapping?.specKey || '',
        target: {
          modelSlug,
          sectionCode,
          position: String(part.position || ''),
          partNumber: part.partNumber || '',
          description: part.description,
          relation: mapping?.relation || 'component',
        },
        proposedSpecification,
        reason,
      });
      setStatus('Thank you! Your contribution was sent to the Help Me DIY admin for review.');
      onSubmitted?.();
    } catch (error) {
      setStatus(error.message || 'Failed to submit your contribution.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="service-contribution-overlay" role="dialog" aria-modal="true" aria-label="Contribute a Service Spec">
      <form className="service-contribution-modal" onSubmit={submit}>
        <div className="service-contribution-heading">
          <div>
            <span>Help the community</span>
            <h3>{existing ? 'Suggest a Service Spec correction' : 'Submit a missing Service Spec'}</h3>
            <p>{part.partNumber || part.position} — {part.description}</p>
          </div>
          <button type="button" className="service-contribution-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="service-contribution-grid">
          <label>Component<input required value={form.component} onChange={(e) => change('component', e.target.value)} /></label>
          <label>Connection / procedure<input required value={form.connection} onChange={(e) => change('connection', e.target.value)} placeholder="e.g. Water pump to engine" /></label>
          <label>Fastener type<input value={form.fastener.type} onChange={(e) => changeNested('fastener', 'type', e.target.value)} /></label>
          <label>Fastener size<input value={form.fastener.size} onChange={(e) => changeNested('fastener', 'size', e.target.value)} placeholder="e.g. M8 x 35" /></label>
        </div>

        <fieldset className="service-contribution-steps">
          <legend>Service value or stages</legend>
          {form.steps.map((step, index) => (
            <div className="service-contribution-step" key={`${index}-${step.kind}`}>
              <label>Kind<input required value={step.kind} onChange={(e) => changeStep(index, 'kind', e.target.value)} /></label>
              <label>Value<input required type="number" step="any" value={step.value} onChange={(e) => changeStep(index, 'value', e.target.value)} /></label>
              <label>Unit<input required value={step.unit} onChange={(e) => changeStep(index, 'unit', e.target.value)} /></label>
              <label>Label<input value={step.label} onChange={(e) => changeStep(index, 'label', e.target.value)} placeholder="Stage 1 / New" /></label>
              <label className="service-contribution-wide">Display text<input required value={step.originalText} onChange={(e) => changeStep(index, 'originalText', e.target.value)} placeholder="e.g. 23 Nm (17 ft-lb)" /></label>
              {form.steps.length > 1 && <button type="button" onClick={() => change('steps', form.steps.filter((_, i) => i !== index))}>Remove stage</button>}
            </div>
          ))}
          <button type="button" onClick={() => change('steps', [...form.steps, blankStep()])}>+ Add stage</button>
        </fieldset>

        <div className="service-contribution-grid">
          <label>Model year start<input type="number" value={form.applicability.yearStart} onChange={(e) => changeNested('applicability', 'yearStart', e.target.value)} /></label>
          <label>Model year end<input type="number" value={form.applicability.yearEnd} onChange={(e) => changeNested('applicability', 'yearEnd', e.target.value)} /></label>
          <label className="service-contribution-wide">Applicability notes<textarea rows={2} value={form.applicability.qualifiers} onChange={(e) => changeNested('applicability', 'qualifiers', e.target.value)} placeholder="One note per line" /></label>
          <label className="service-contribution-wide">Conditions<textarea rows={2} value={form.conditions} onChange={(e) => change('conditions', e.target.value)} placeholder="One condition per line" /></label>
          <label className="service-contribution-wide">Cautions<textarea rows={2} value={form.cautions} onChange={(e) => change('cautions', e.target.value)} placeholder="One caution per line" /></label>
        </div>

        <fieldset className="service-contribution-source">
          <legend>Source citation</legend>
          <div className="service-contribution-grid">
            <label>Document or manual<input required value={form.source.document} onChange={(e) => changeNested('source', 'document', e.target.value)} /></label>
            <label>Page<input required type="number" min="1" value={form.source.pdfPage} onChange={(e) => changeNested('source', 'pdfPage', e.target.value)} /></label>
            <label className="service-contribution-wide">Manual section<input value={form.source.manualSection} onChange={(e) => changeNested('source', 'manualSection', e.target.value)} /></label>
            <label className="service-contribution-wide">Exact supporting quote<textarea required rows={3} value={form.source.quote} onChange={(e) => changeNested('source', 'quote', e.target.value)} /></label>
          </div>
        </fieldset>

        <label className="service-contribution-reason">Why should this be changed or added?<textarea required rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        {status && <div className={status.startsWith('Thank') ? 'success-banner' : 'error-banner'}>{status}</div>}
        <div className="service-contribution-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary" disabled={busy}>{busy ? 'Submitting...' : 'Submit for admin review'}</button>
        </div>
      </form>
    </div>
  );
};

export default ServiceSpecContributionModal;
